
import React, { useState, useEffect } from 'react';
import { User } from '@/api/entities';
import { useUser, useCurrentTenant } from '@/contexts/UserContext';
import { resolveEffectiveTenant } from '@/lib/tenantUtils';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/components/use-toast'; // Corrected import path
import { Plus, Trash2, Save, AlertCircle, CheckCircle2, Loader2, Calendar } from 'lucide-react';
import { useTranslation } from '@/components/utils/translations';

const API_BASE_URL = window.location.hostname === 'localhost' 
  ? 'https://api.neogtm.com' 
  : 'https://api.neogtm.com';

const DAYS = [
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
  'Sunday',
];

/** Weekdays only — default windows stay Mon–Fri 9–5 until the user adds weekend rows. */
const DEFAULT_COMMS_WINDOW_DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'];

/** Default before first save or when server signals `useDefault`: 9am–5pm, Mon–Fri. */
const DEFAULT_COMMS_WINDOWS = DEFAULT_COMMS_WINDOW_DAYS.map((day) => ({
  day,
  start_time: '09:00',
  end_time: '17:00',
}));

const TIMEZONES = [
  'America/New_York',
  'America/Chicago', 
  'America/Denver',
  'America/Los_Angeles',
  'America/Toronto',
  'Australia/Sydney',
  'Australia/Melbourne',
  'Europe/London'
];
const DEFAULT_TIMEZONE = 'Europe/London';

export default function SettingsCommsSchedule({ currentTenant: currentTenantProp }) {
  const { t } = useTranslation();
  const contextUser = useUser();
  const contextTenant = useCurrentTenant();
  const currentTenant = currentTenantProp || contextTenant;
  const tenantId = resolveEffectiveTenant(
    contextUser?.role === 'admin' ? localStorage.getItem('admin-active-tenant') : null,
    currentTenant,
    contextUser,
    'instructure'
  );
  const user = contextUser;
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  /* 🔴 WHAT IS ON SCREEN IS NOT NECESSARILY WHAT IS SAVED. Only 6 of 46 users across the live
   * tenants have a comms_schedule row, yet this page renders Mon–Fri / Europe/London and then
   * labels it "Complete" — so a rep believes they configured something they never did, and stops
   * looking. Meanwhile the engine applies its own default (Tue/Wed/Thu, UTC), which is not what
   * they are reading. Tracking this separately is what lets the badge tell the truth. */
  const [isSaved, setIsSaved] = useState(false);
  const [schedule, setSchedule] = useState({
    timezone: DEFAULT_TIMEZONE,
    windows: DEFAULT_COMMS_WINDOWS.map((w) => ({ ...w })),
  });

  const { toast } = useToast();

  useEffect(() => {
    loadSchedule();
  }, [currentTenant, contextUser]);

  const loadSchedule = async () => {
    if (!contextUser) return;
    setIsLoading(true);
    try {
      console.log('🔍 Loading comms schedule for tenant:', tenantId, 'user:', contextUser.id);

      // Try to load from backend API FIRST (source of truth)
      try {
        const url = `${API_BASE_URL}/api/settings/comms-schedule?tenant_id=${tenantId}&user_id=${contextUser.id}`;
        console.log('🔍 Fetching from:', url);
        
        const response = await fetch(url, {
          credentials: 'include',
          headers: {
            'x-user-id': String(contextUser.id),
            'x-tenant-id': String(tenantId),
          },
        });
        
        console.log('🔍 API response status:', response.status, response.ok);

        if (response.ok) {
          const data = await response.json();
          console.log('🔍 API response data:', data);

          if (data.schedule != null && typeof data.schedule === 'object') {
            console.log('✅ Using API schedule:', data.schedule);
            const incoming = data.schedule;
            let windows;
            if (Array.isArray(incoming.windows) && incoming.windows.length > 0) {
              // Persisted user schedule — use as-is (never replace with defaults).
              windows = incoming.windows.map((w) => ({ ...w }));
            } else if (Array.isArray(incoming.windows)) {
              // Explicit [] from server — keep empty (do not revert to Mon–Fri default).
              windows = [];
            } else {
              // No windows field yet / invalid — treat as never configured → Mon–Fri 9–5 default.
              windows = DEFAULT_COMMS_WINDOWS.map((w) => ({ ...w }));
            }
            setSchedule({
              ...incoming,
              timezone: incoming.timezone || DEFAULT_TIMEZONE,
              windows,
            });
            /* A row came back with real windows — this one is genuinely theirs. */
            setIsSaved(Array.isArray(incoming.windows) && incoming.windows.length > 0);
            setIsLoading(false);
            return;
          }

          if (data.useDefault === true) {
            console.log('ℹ️ Using client default schedule (server: useDefault=true)');
            setSchedule({
              timezone: DEFAULT_TIMEZONE,
              windows: DEFAULT_COMMS_WINDOWS.map((w) => ({ ...w })),
            });
            setIsLoading(false);
            return;
          }

          console.log('⚠️ API returned no schedule object (unexpected shape), using client defaults:', data);
          setSchedule({
            timezone: DEFAULT_TIMEZONE,
            windows: DEFAULT_COMMS_WINDOWS.map((w) => ({ ...w })),
          });
          setIsLoading(false);
          return;
        } else {
          console.log('❌ API response not ok:', response.status);
        }
      } catch (apiError) {
        console.log('❌ API error:', apiError.message);
      }

      // Fallback: Load from user profile (old Base44 storage)
      console.log('📥 Falling back to Base44 user profile');
      if (contextUser.comms_schedule) {
        console.log('⚠️ Using OLD Base44 schedule:', contextUser.comms_schedule);
        const cs = contextUser.comms_schedule;
        let windows;
        if (Array.isArray(cs.windows) && cs.windows.length > 0) {
          windows = cs.windows.map((w) => ({ ...w }));
        } else if (Array.isArray(cs.windows)) {
          windows = [];
        } else {
          windows = DEFAULT_COMMS_WINDOWS.map((w) => ({ ...w }));
        }
        setSchedule({
          ...cs,
          timezone: cs.timezone || DEFAULT_TIMEZONE,
          windows,
        });
      }

    } catch (error) {
      console.error('Error loading schedule:', error);
      toast({
        title: t('error'),
        description: t('failedToLoadSchedule'),
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async () => {
    if (!user) return;
    setIsSaving(true);
    try {
      console.log('💾 DEBUG:', {
        currentTenant: currentTenant,
        userTenantId: user?.tenant_id,
        finalTenantId: tenantId
      });
      // Primary: Try to save to backend API
      try {
        const response = await fetch(`${API_BASE_URL}/api/settings/comms-schedule`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'x-user-id': String(user.id),
            'x-tenant-id': String(tenantId),
          },
          credentials: 'include',
          body: JSON.stringify({
            user_id: user.id,
            tenant_id: tenantId,
            timezone: schedule.timezone,
            windows: schedule.windows,
          })
        });

        if (response.ok) {
          toast({
            title: t('scheduleSaved'),
            description: t('communicationScheduleUpdated'),
          });
          setIsSaving(false);
          return;
        }
      } catch (apiError) {
        console.log('API not available, saving to user profile instead');
      }

      // Fallback: Save to user profile in Base44
      await User.updateMyUserData({ comms_schedule: schedule });
      
      toast({
        title: t('scheduleSaved'),
        description: t('communicationScheduleUpdated'),
      });

    } catch (error) {
      console.error('Error saving schedule:', error);
      toast({
        title: t('error'),
        description: t('failedToSaveSchedule'),
        variant: 'destructive'
      });
    } finally {
      setIsSaving(false);
    }
  };

  const addWindow = () => {
    setSchedule(prev => ({
      ...prev,
      windows: [...prev.windows, { day: 'Monday', start_time: '09:00', end_time: '17:00' }]
    }));
  };

  const updateWindow = (index, field, value) => {
    setSchedule(prev => ({
      ...prev,
      windows: prev.windows.map((window, i) => 
        i === index ? { ...window, [field]: value } : window
      )
    }));
  };

  const removeWindow = (index) => {
    if (schedule.windows.length <= 1) {
      toast({
        title: t('atLeastOneWindowRequired'),
        description: t('addWindowBeforeRemoving'),
        variant: 'destructive',
      });
      return;
    }
    setSchedule((prev) => ({
      ...prev,
      windows: prev.windows.filter((_, i) => i !== index),
    }));
  };

  const isFilledIn = schedule.windows.length > 0 &&
                     schedule.windows.every(w => w.day && w.start_time && w.end_time);
  /* "Complete" now means SAVED and filled in. A default that merely looks complete is reported as
   * not set up yet, because that is what it is — and the rep can fix it in one click. */
  const isComplete = isFilledIn && isSaved;

  if (!contextUser) {
    return (
      <Card className="border-0 shadow-md">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="w-5 h-5 text-[#00C2C2]" />
            {t('communicationSchedule')}
          </CardTitle>
          <CardDescription>{t('configureCommsSchedule')}</CardDescription>
          <p className="text-xs text-gray-500 mt-1 max-w-2xl">{t('commsScheduleHelp')}</p>
        </CardHeader>
        <CardContent className="min-h-[400px] flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-[#00C2C2]" />
        </CardContent>
      </Card>
    );
  }

  if (isLoading) {
    return (
      <Card className="border-0 shadow-md">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="w-5 h-5 text-[#00C2C2]" />
            {t('communicationSchedule')}
          </CardTitle>
          <CardDescription>{t('configureCommsSchedule')}</CardDescription>
          <p className="text-xs text-gray-500 mt-1 max-w-2xl">{t('commsScheduleHelp')}</p>
        </CardHeader>
        <CardContent className="min-h-[400px] flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-[#00C2C2]" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-0 shadow-md">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="w-5 h-5 text-[#00C2C2]" />
              {t('communicationSchedule')}
            </CardTitle>
            <CardDescription>{t('configureCommsSchedule')}</CardDescription>
            <p className="text-xs text-gray-500 mt-1 max-w-2xl">{t('commsScheduleHelp')}</p>
            {/* 🔴 SAY WHAT THEY ACTUALLY GET UNTIL THEY SAVE. The times below are this page's
                suggestion; the engine's own fallback is Tuesday to Thursday in UTC, so a rep who
                never presses Save is on a different schedule from the one they are reading. That
                gap is the entire reason this notice exists. */}
            {!isSaved && !isLoading && (
              <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-2 mt-2 max-w-2xl">
                These are suggested times — you have not saved a schedule yet. Until you do, NEO
                schedules on its own default of <strong>Tuesday to Thursday, 09:00–11:00 and
                14:00–16:00 UTC</strong>, which may not be what you see here. Press Save Schedule to
                make these times yours.
              </p>
            )}
          </div>
          {/* THREE STATES, NOT TWO. "Complete" and "Incomplete" could not express the case that
              actually applies to almost everybody: filled in on screen, never saved, and therefore
              not what the engine is using. Red would be alarming and still wrong — nothing is
              broken, it simply has not been saved. */}
          {isComplete ? (
            <Badge className="bg-green-100 text-green-800">
              <CheckCircle2 className="w-3 h-3 mr-1" />
              {t('complete')}
            </Badge>
          ) : isFilledIn ? (
            <Badge className="bg-amber-100 text-amber-900">
              <AlertCircle className="w-3 h-3 mr-1" />
              Not saved yet
            </Badge>
          ) : (
            <Badge variant="destructive">
              <AlertCircle className="w-3 h-3 mr-1" />
              {t('incomplete')}
            </Badge>
          )}
        </div>
      </CardHeader>
      <CardContent className="min-h-[400px] space-y-6">
        {/* Timezone */}
        <div className="space-y-2">
          <Label>{t('timezone')}</Label>
          <Select 
            value={schedule.timezone} 
            onValueChange={(value) => setSchedule(prev => ({ ...prev, timezone: value }))}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {TIMEZONES.map(tz => (
                <SelectItem key={tz} value={tz}>{tz}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Windows */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <Label>{t('availabilityWindows')}</Label>
            <Button variant="outline" size="sm" onClick={addWindow}>
              <Plus className="w-4 h-4 mr-1" />
              {t('addWindow')}
            </Button>
          </div>

          {schedule.windows.map((window, index) => (
            <Card key={index} className="p-4">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                <div className="space-y-2">
                  <Label className="text-xs">{t('day')}</Label>
                  <Select 
                    value={window.day} 
                    onValueChange={(value) => updateWindow(index, 'day', value)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {DAYS.map(day => (
                        <SelectItem key={day} value={day}>{day}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label className="text-xs">{t('startTime')}</Label>
                  <Input
                    type="time"
                    value={window.start_time}
                    onChange={(e) => updateWindow(index, 'start_time', e.target.value)}
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-xs">{t('endTime')}</Label>
                  <Input
                    type="time"
                    value={window.end_time}
                    onChange={(e) => updateWindow(index, 'end_time', e.target.value)}
                  />
                </div>

                <div className="flex items-end">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => removeWindow(index)}
                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>

        <Button 
          onClick={handleSave} 
          disabled={isSaving || !isFilledIn}
          className="w-full bg-[#00C2C2] hover:bg-[#00A3A3]"
        >
          {isSaving ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              {t('saving')}
            </>
          ) : (
            <>
              <Save className="w-4 h-4 mr-2" />
              {t('saveSchedule')}
            </>
          )}
        </Button>
      </CardContent>
    </Card>
  );
}
